import { toast } from 'react-toastify';
import { Button } from '@accure/ui';
import { AlertTriangle, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/dialog';

import type { User } from '../entities/user';
import { DeleteUserMutation } from '../mutations/delete-user';

export type DeleteUserConfirmationDialogProps = {
  user: User | undefined;
  onClose: () => void;
};

export function DeleteUserConfirmationDialog({
  user,
  onClose,
}: DeleteUserConfirmationDialogProps) {
  const deleteUser = DeleteUserMutation.useMutation();

  async function handleDelete() {
    if (!user) {
      return;
    }

    await toast.promise(deleteUser.mutateAsync({ id: user.id }), {
      pending: 'Deleting user...',
      success: 'User deleted successfully!',
      error: 'Failed to delete user. Please try again.',
    });

    onClose();
  }

  return (
    <Dialog open={Boolean(user)} onClose={onClose}>
      {user ? (
        <>
          <DialogTitle>
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="size-5 text-red-600" />
              </div>
              <span>
                Delete {user.firstName} {user.lastName}?
              </span>
            </div>
          </DialogTitle>
          <DialogBody>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete the user? This action cannot be
              undone.
            </p>
          </DialogBody>
          <DialogActions>
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              type="button"
              onClick={handleDelete}
              disabled={deleteUser.isLoading}
            >
              {deleteUser.isLoading ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Deleting
                </>
              ) : (
                'Delete User'
              )}
            </Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}
